import {
    Component,
    ViewChild,
    OnInit,
    AfterViewInit,
    OnDestroy,
    ChangeDetectorRef,
} from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import {
    Router,
    RouterOutlet,
    NavigationEnd,
    ActivatedRoute,
} from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FeathericonsModule } from '../../icons/feathericons/feathericons.module';
import { ModuleService, Module } from '../../services/module.service';
import { LoggerService } from '../../core/services/logger.service';
import { catchError, finalize, filter } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { DeleteModuleDialogComponent } from './delete-module-dialog/delete-module-dialog.component';

@Component({
    selector: 'app-modules-page',
    imports: [
        CommonModule,
        NgIf,
        RouterOutlet,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatPaginatorModule,
        MatTableModule,
        MatSortModule,
        MatSnackBarModule,
        MatDialogModule,
        MatTooltipModule,
        FeathericonsModule,
    ],
    templateUrl: './modules-page.component.html',
    styleUrls: ['./modules-page.component.scss'],
})
export class ModulesPageComponent implements OnInit, AfterViewInit, OnDestroy {
    displayedColumns: string[] = [
        'name',
        'code',
        'menuGroup',
        'order',
        'icon',
        'status',
        'action',
    ];
    dataSource = new MatTableDataSource<ModuleElement>([]);
    isLoading = false;
    private routerSubscription?: Subscription;
    private paginationRestored = false;
    private pendingPaginationState: { page: number; pageSize: number } | null =
        null;

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private moduleService: ModuleService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        // eslint-disable-next-line no-unused-vars
        private dialog: MatDialog,
        // eslint-disable-next-line no-unused-vars
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        const queryParams = this.route.snapshot.queryParams;
        if (queryParams['page'] || queryParams['pageSize']) {
            const page = parseInt(queryParams['page'] || '1', 10);
            const pageSize = parseInt(queryParams['pageSize'] || '10', 10);
            this.pendingPaginationState = { page, pageSize };
            this.logger.info(
                `Estado de paginación pendiente detectado: page=${page}, pageSize=${pageSize}`,
                'ModulesPageComponent'
            );
        }
        this.loadModules();
        this.subscribeToRouterEvents();
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }

    private subscribeToRouterEvents(): void {
        this.routerSubscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                const url = event.urlAfterRedirects;
                if (url === '/modules' || url.startsWith('/modules?')) {
                    this.logger.info(
                        'Navegación detectada a /modules, refrescando lista de módulos',
                        'ModulesPageComponent'
                    );
                    this.paginationRestored = false;

                    const queryParams = this.route.snapshot.queryParams;
                    if (queryParams['page'] || queryParams['pageSize']) {
                        const page = parseInt(queryParams['page'] || '1', 10);
                        const pageSize = parseInt(
                            queryParams['pageSize'] || '10',
                            10
                        );
                        this.pendingPaginationState = { page, pageSize };
                        this.logger.info(
                            `Estado de paginación pendiente: page=${page}, pageSize=${pageSize}`,
                            'ModulesPageComponent'
                        );
                    }

                    this.loadModules();
                }
            });
    }

    ngAfterViewInit(): void {
        if (this.paginator) {
            this.dataSource.paginator = this.paginator;
        }
        if (this.sort) {
            this.dataSource.sort = this.sort;
        }
        this.dataSource.sortingDataAccessor = (
            item: ModuleElement,
            property: string
        ): string => {
            switch (property) {
                case 'name':
                    return item.name;
                case 'code':
                    return item.code;
                case 'menuGroup':
                    return item.menuGroup || '';
                case 'order':
                    return item.order.toString();
                case 'icon':
                    return item.icon || '';
                case 'status':
                    return item.status;
                default:
                    return (
                        (item as unknown as Record<string, string>)[property] ??
                        ''
                    );
            }
        };
    }

    loadModules(): void {
        this.logger.info(
            'Iniciando carga de módulos desde el backend',
            'ModulesPageComponent'
        );
        this.moduleService
            .getModules(true)
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al cargar módulos',
                        error as Error,
                        'ModulesPageComponent'
                    );
                    this.snackBar.open('Error al cargar módulos', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar'],
                    });
                    return of({ success: false, data: [] });
                }),
                // eslint-disable-next-line no-unused-vars
                finalize(() => {})
            )
            .subscribe((response) => {
                this.logger.info(
                    `Respuesta del backend: ${JSON.stringify(response)}`,
                    'ModulesPageComponent'
                );
                if (response.success && response.data) {
                    const moduleElements = response.data.map((module: Module) =>
                        this.mapModuleToModuleElement(module)
                    );
                    this.logger.info(
                        `Mapeados ${moduleElements.length} módulos para la tabla`,
                        'ModulesPageComponent'
                    );

                    this.dataSource.data = moduleElements;

                    if (this.paginator) {
                        const savedPageSize = this.paginator.pageSize;

                        this.dataSource.paginator = null;
                        this.dataSource.paginator = this.paginator;

                        if (savedPageSize !== 10) {
                            this.paginator.pageSize = savedPageSize;
                        }

                        this.logger.info(
                            `Paginador reasignado - Length: ${this.paginator.length}, DataSource data length: ${this.dataSource.data.length}`,
                            'ModulesPageComponent'
                        );
                    }

                    this.cdr.detectChanges();

                    setTimeout(() => {
                        this.restorePaginationStateAfterInit();
                    }, 100);
                } else {
                    this.logger.warn(
                        'Respuesta del backend no exitosa o sin datos',
                        'ModulesPageComponent'
                    );
                }
            });
    }

    editModule(moduleElement: ModuleElement): void {
        this.logger.info(
            `Editar módulo: ${moduleElement.name}`,
            'ModulesPageComponent'
        );
        this.moduleService
            .getModules(true)
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al obtener datos del módulo',
                        error as Error,
                        'ModulesPageComponent'
                    );
                    this.snackBar.open(
                        'Error al obtener datos del módulo',
                        'Cerrar',
                        { duration: 5000, panelClass: ['error-snackbar'] }
                    );
                    return of({ success: false, data: [] });
                })
            )
            .subscribe((response) => {
                if (response.success && response.data) {
                    const module = response.data.find(
                        (m: Module) => m.code === moduleElement.code
                    );
                    if (module) {
                        const queryParams: any = {};
                        if (this.paginator) {
                            const currentPage = this.paginator.pageIndex;
                            const pageSize = this.paginator.pageSize;
                            if (currentPage > 0) {
                                queryParams.page = (currentPage + 1).toString();
                            }
                            if (pageSize !== 10) {
                                queryParams.pageSize = pageSize.toString();
                            }
                        }
                        this.router.navigate(['/modules/edit', module.id], {
                            queryParams,
                        });
                    } else {
                        this.snackBar.open('Módulo no encontrado', 'Cerrar', {
                            duration: 3000,
                            panelClass: ['error-snackbar'],
                        });
                    }
                }
            });
    }

    deleteModule(moduleElement: ModuleElement): void {
        this.logger.info(
            `Eliminar módulo: ${moduleElement.name}`,
            'ModulesPageComponent'
        );
        this.moduleService
            .getModules(true)
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al obtener datos del módulo',
                        error as Error,
                        'ModulesPageComponent'
                    );
                    this.snackBar.open(
                        'Error al obtener datos del módulo',
                        'Cerrar',
                        { duration: 5000, panelClass: ['error-snackbar'] }
                    );
                    return of({ success: false, data: [] });
                })
            )
            .subscribe((response) => {
                if (response.success && response.data) {
                    const module = response.data.find(
                        (m: Module) => m.code === moduleElement.code
                    );
                    if (module) {
                        this.openDeleteConfirmationDialog(module);
                    } else {
                        this.snackBar.open('Módulo no encontrado', 'Cerrar', {
                            duration: 3000,
                            panelClass: ['error-snackbar'],
                        });
                    }
                }
            });
    }

    private openDeleteConfirmationDialog(module: Module): void {
        const dialogRef = this.dialog.open(DeleteModuleDialogComponent, {
            width: '500px',
            maxWidth: '90vw',
            disableClose: true,
            data: { module },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.performDeleteModule(module);
            }
        });
    }

    private performDeleteModule(module: Module): void {
        this.logger.info(
            `Iniciando eliminación del módulo: ${module.name} (ID: ${module.id})`,
            'ModulesPageComponent'
        );
        this.isLoading = true;
        this.moduleService
            .deleteModule(module.id)
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al eliminar módulo',
                        error as Error,
                        'ModulesPageComponent'
                    );
                    const errorMessage =
                        (error as any).error?.message ||
                        'Error al eliminar el módulo';
                    this.snackBar.open(errorMessage, 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar'],
                    });
                    this.isLoading = false;
                    return of(null);
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((response) => {
                this.logger.info(
                    `Respuesta de eliminación: ${JSON.stringify(response)}`,
                    'ModulesPageComponent'
                );
                if (response && response.success) {
                    // Notificar que los módulos han cambiado
                    this.moduleService.notifyModulesChanged();

                    this.logger.info(
                        'Módulo eliminado exitosamente, recargando lista',
                        'ModulesPageComponent'
                    );
                    this.loadModules();
                    this.snackBar.open(
                        'Módulo eliminado exitosamente',
                        'Cerrar',
                        { duration: 3000, panelClass: ['success-snackbar'] }
                    );
                } else {
                    this.logger.warn(
                        'Respuesta de eliminación no exitosa',
                        'ModulesPageComponent'
                    );
                }
            });
    }

    onMenuGroups(): void {
        this.router.navigate(['/modules/menu-groups']);
    }

    onReportCategories(): void {
        this.router.navigate(['/modules/report-categories']);
    }

    onCreateModule(): void {
        this.router.navigate(['/modules/create']);
    }

    private mapModuleToModuleElement(module: Module): ModuleElement {
        return {
            name: module.name,
            code: module.code,
            menuGroup: module.menuGroup || 'Sin grupo',
            order: module.order,
            icon: module.icon || '',
            status: module.isActive ? 'active' : 'inactive',
        };
    }

    isBaseRoute(): boolean {
        const url = this.router.url || '';
        return (
            url === '/modules' ||
            url === '/modules/' ||
            url.startsWith('/modules?')
        );
    }

    isCreateRoute(): boolean {
        const url = this.router.url || '';
        return url.startsWith('/modules/create');
    }

    private restorePaginationStateAfterInit(): void {
        if (this.paginationRestored) {
            return;
        }

        const stateToRestore =
            this.pendingPaginationState ||
            (() => {
                const queryParams = this.route.snapshot.queryParams;
                if (queryParams['page'] || queryParams['pageSize']) {
                    return {
                        page: parseInt(queryParams['page'] || '1', 10),
                        pageSize: parseInt(queryParams['pageSize'] || '10', 10),
                    };
                }
                return null;
            })();

        if (!stateToRestore) {
            return;
        }

        if (
            !this.paginator ||
            !this.dataSource ||
            this.dataSource.data.length === 0
        ) {
            this.logger.debug(
                'Paginador o datos no disponibles aún, reintentando...',
                'ModulesPageComponent'
            );
            setTimeout(() => {
                this.restorePaginationStateAfterInit();
            }, 50);
            return;
        }

        const { page, pageSize } = stateToRestore;

        this.logger.info(
            `Restaurando estado de paginación: page=${page}, pageSize=${pageSize}, totalItems=${this.dataSource.data.length}`,
            'ModulesPageComponent'
        );

        if (pageSize !== 10) {
            this.paginator.pageSize = pageSize;
        }

        const totalPages = Math.ceil(
            this.dataSource.data.length / this.paginator.pageSize
        );
        if (page > 1 && page <= totalPages) {
            this.paginator.pageIndex = page - 1;
        } else if (page > totalPages && totalPages > 0) {
            this.paginator.pageIndex = Math.max(0, totalPages - 1);
        }

        this.paginationRestored = true;
        this.pendingPaginationState = null;

        this.cdr.detectChanges();

        this.router
            .navigate([], {
                relativeTo: this.route,
                queryParams: {},
                replaceUrl: true,
            })
            .catch(() => {});
    }
}

export interface ModuleElement {
    name: string;
    code: string;
    menuGroup: string;
    order: number;
    icon: string;
    status: string;
}
